// packages/auth/src/general-access.ts
import type { Role } from './role.js'
import type { Scope } from './scope.js'

/**
 * ★ PHASE 10/11 — the handful of Annex A stories whose actor is either "any
 * signed-in officer" (24, 25, 39, 48) or a named subset that is neither
 * PSD-only nor a single-context administration surface (49, 51). Kept
 * separate from `administration-access.ts` (which is entirely PSD-only, one
 * exception each for MBIM_USER and ENTITLEMENT_USER) and from
 * `reference-access.ts` (a per-parameter table) because none of these stories
 * is a reference-data or administration capability — they are product-wide
 * services every human identity may reach, or a named cross-agency group.
 *
 * ⚠ `CAM_AGENT` and `PRS_SERVICE` are never in `HUMAN_ROLES`. Neither is a
 * signed-in officer: `CAM_AGENT` authenticates through a separate
 * shared-credential gate (`requireCamAgent`) and never holds a PRS2 session
 * cookie at all, and `PRS_SERVICE` is a machine identity for the same reason
 * `mayReadFullAuditTrail` and every other capability in this package name
 * roles explicitly rather than "everyone with a session" — a set that is
 * total by construction cannot silently widen the day a new machine role is
 * added.
 */
const HUMAN_ROLES: ReadonlySet<Role> = new Set<Role>([
  'HSP_USER',
  'MBIM_USER',
  'ENTITLEMENT_USER',
  'RECOVERY_USER',
  'PAYMENT_USER',
  'MINISTRY_HR_USER',
  'PSD_USER',
  'MMBA_USER',
  'SYSTEM_ADMIN',
])

/**
 * Annex A stories 24 ("View Online Help") and 25 ("Download User Manual") —
 * every human role the stories name, without exception.
 */
export function mayUseHelpAndManual(scope: Scope): boolean {
  return HUMAN_ROLES.has(scope.role)
}

/**
 * Annex A story 39 ("General FAQ or Enquiry Chatbot") — the identical actor
 * list as stories 24/25.
 */
export function mayUseFaq(scope: Scope): boolean {
  return HUMAN_ROLES.has(scope.role)
}

/**
 * Annex A story 48 ("WOG ITSM Helpdesk Ticketing System Integration") — the
 * identical actor list again: any signed-in officer may raise a support
 * ticket about the system they are using.
 */
export function mayRaiseSupportTicket(scope: Scope): boolean {
  return HUMAN_ROLES.has(scope.role)
}

/**
 * ★ PHASE 10 — Annex A story 49 ("Print CS Card Information"). Story 49's own
 * text names exactly one actor: "User (Ministry/Agency HR User)" — the same
 * "no reader beyond who the story itself names" discipline
 * `administration-access.ts` follows throughout, applied to a role that is
 * not PSD.
 */
const MINISTRY_HR_ONLY: ReadonlySet<Role> = new Set<Role>(['MINISTRY_HR_USER'])

export function mayPrintCsCard(scope: Scope): boolean {
  return MINISTRY_HR_ONLY.has(scope.role)
}

/**
 * ★ PHASE 10 — Annex A story 51 ("Financial Counselling Access and Cost
 * Estimation"). Story 51's own text names five actors, explicitly excluding
 * `MBIM_USER`, `MINISTRY_HR_USER` and `MMBA_USER` — narrower than the
 * story-24/25/39/48 set above, and kept as its own function rather than
 * folded into `HUMAN_ROLES` for exactly that reason.
 */
const FINANCIAL_COUNSELLING_ROLES: ReadonlySet<Role> = new Set<Role>([
  'HSP_USER',
  'ENTITLEMENT_USER',
  'RECOVERY_USER',
  'PAYMENT_USER',
  'PSD_USER',
])

export function mayAccessFinancialCounselling(scope: Scope): boolean {
  return FINANCIAL_COUNSELLING_ROLES.has(scope.role)
}
